import { useUpdatePin } from '@lessonup/pin-renderer';
import { CategoryAreaPinComponent } from '@lessonup/pins-shared';
import {
  Button,
  IconAdd,
  IconEdit,
  IconMove,
  IconRemove,
  IconViewGrid,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import React, { useCallback, useMemo } from 'react';
import {
  ContextSidebarCollapsibleGroup,
  ContextSidebarGroup,
} from '../../../../../../../../components/ContextSidebar/ContextSidebarGroup';
import { useEditorContext } from '../../../../../../context/EditorContext/EditorContext';
import {
  useEditMode,
  useEffectiveActivePinComponentSettings,
  useNonNullActivePin,
} from '../../../../../../context/EditorContext/hooks/useEditorState';
import { useEditorTranslation } from '../../../../../../hooks/useEditorTranslation';
import { addNewCategoryAction } from '../../../../../../utils/pin/categoryAreaPinComponentSettings';
import { SettingsToggle } from '../../../../../SettingsToggle/SettingsToggle';
import { EditorTiptapInput } from '../../../EditorInputFields/EditorTiptapInput';
import { BackgroundColorSetting } from '../../sharedComponentSettings/BackgroundColorSetting';
import { BorderColorSetting } from '../../sharedComponentSettings/BorderColorSetting';
import { BorderRadiusSetting } from '../../sharedComponentSettings/BorderRadiusSetting';
import { BorderSizeSetting } from '../../sharedComponentSettings/BorderSizeSetting';
import { BorderStyleSetting } from '../../sharedComponentSettings/BorderStyleSetting';
import { LayoutPositionSettingGroup } from '../../sharedComponentSettings/LayoutPositionSetting';
import { PinComponentSettingsProps } from '../../types';
import { CategoryAreaBackgroundImageSettings } from './CategoryAreaBackgroundImageSettings';
import { CategoryComponentSettings } from './CategoryComponentSettings';

const MINIMUM_CATEGORY_COUNT = 2;

export const CategoryAreaPinComponentSettings: React.FC<PinComponentSettingsProps<CategoryAreaPinComponent>> = ({
  pinComponent,
}) => {
  const { t } = useEditorTranslation();
  const dispatch = useUpdatePin();
  const editMode = useEditMode();
  const { setModeSelecting, setModeCategoryEdit } = useEditorContext();
  const pin = useNonNullActivePin();
  const categoryAreaSettings = useEffectiveActivePinComponentSettings('CATEGORY_AREA', {
    settings: pinComponent.settings,
  });

  const categories = useMemo(() => categoryAreaSettings.categories || [], [categoryAreaSettings.categories]);
  const layoutMode = categoryAreaSettings.layoutMode || 'AUTO';

  const activeCategoryId =
    editMode.type === 'categoryEdit' || editMode.type === 'textEdit' ? editMode.activeCategoryId : undefined;
  const activeCategory = activeCategoryId ? categories.find((cat) => cat.id === activeCategoryId) : undefined;

  const removeCategory = useCallback(
    (categoryId: string) => {
      if (categories.length <= MINIMUM_CATEGORY_COUNT) return;

      const updatedCategories = categories.filter((cat) => cat.id !== categoryId);

      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          categories: updatedCategories,
        },
      });
    },
    [dispatch, pinComponent.id, categories]
  );

  const updateLayoutMode = useCallback(
    (newLayoutMode: 'AUTO' | 'CUSTOM') => {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          layoutMode: newLayoutMode,
        },
      });
    },
    [dispatch, pinComponent.id]
  );

  const handleBackToContainer = useCallback(() => {
    setModeSelecting([pinComponent.id]);
  }, [setModeSelecting, pinComponent.id]);

  const handleSelectCategory = useCallback(
    (categoryId: string) => {
      setModeCategoryEdit(pinComponent.id, categoryId);
    },
    [setModeCategoryEdit, pinComponent.id]
  );

  if (activeCategory) {
    return (
      <CategoryComponentSettings
        category={activeCategory}
        pinComponent={pinComponent}
        onBackToContainer={handleBackToContainer}
      />
    );
  }

  return (
    <>
      <ContextSidebarCollapsibleGroup
        persistId="categoryAreaLayout"
        title={t('contextSidebar.categoryArea.manageCategories')}
        isCollapsed={false}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <ContextSidebarGroup>
          <SpaceBetween direction="y" spacing={spacing.size16}>
            <SpaceBetween direction="y" spacing={spacing.size8}>
              <TextElement textStyle="label" size="medium" weight="bold">
                {t('contextSidebar.categoryArea.layoutMode.description')}
              </TextElement>
              <SpaceBetween direction="x" spacing={spacing.size4} alignItems="center">
                <SettingsToggle
                  type="checkbox"
                  toggleVariant="caption"
                  caption={t('contextSidebar.categoryArea.layoutMode.autoGrid')}
                  onChange={() => updateLayoutMode('AUTO')}
                  checked={layoutMode === 'AUTO'}
                  icon={<IconViewGrid />}
                />
                <SettingsToggle
                  type="checkbox"
                  toggleVariant="caption"
                  caption={t('contextSidebar.categoryArea.layoutMode.customLayout')}
                  onChange={() => updateLayoutMode('CUSTOM')}
                  checked={layoutMode === 'CUSTOM'}
                  icon={<IconMove />}
                />
              </SpaceBetween>
            </SpaceBetween>

            <SpaceBetween direction="y" spacing={spacing.size12}>
              {categories.map((category, index) => (
                <StyledCategoryRow key={category.id}>
                  <StyledTextFieldWrapper>
                    <EditorTiptapInput
                      pinId={pin.id}
                      pinComponentId={category.id}
                      fragmentKey={`${category.id}-text`}
                      label={index === 0 ? t('contextSidebar.categoryArea.categoryNames') : undefined}
                      placeholder={t('contextSidebar.categoryArea.defaultCategoryName', {
                        count: index + 1,
                      })}
                      useBaseExtensions={true}
                    />
                  </StyledTextFieldWrapper>
                  <Button
                    onClick={() => handleSelectCategory(category.id)}
                    buttonType="neutral"
                    size="small"
                    iconStart={<IconEdit />}
                    title={t('contextSidebar.categoryArea.editCategory')}
                  />
                  <Button
                    onClick={() => removeCategory(category.id)}
                    disabled={categories.length <= MINIMUM_CATEGORY_COUNT}
                    title={
                      categories.length <= MINIMUM_CATEGORY_COUNT
                        ? t('contextSidebar.categoryArea.minimumCategories', {
                            count: MINIMUM_CATEGORY_COUNT,
                          })
                        : t('contextSidebar.categoryArea.removeCategory')
                    }
                    buttonType="negative"
                    size="small"
                    iconStart={<IconRemove />}
                  />
                </StyledCategoryRow>
              ))}

              <Button
                onClick={() =>
                  dispatch(
                    addNewCategoryAction(
                      pinComponent,
                      `${t('contextSidebar.categoryArea.category', { number: categories.length + 1 })}`
                    )
                  )
                }
                buttonType="neutral"
                showStroke
                size="medium"
                iconStart={<IconAdd />}
                resizing="fixed"
              >
                {t('contextSidebar.categoryArea.addCategory')}
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </ContextSidebarGroup>
      </ContextSidebarCollapsibleGroup>

      <LayoutPositionSettingGroup />

      <ContextSidebarCollapsibleGroup
        persistId="categoryAreaStyling"
        title={t('contextSidebar.categoryArea.backgroundSettings')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="y" spacing={spacing.size8}>
          <BackgroundColorSetting pinComponent={pinComponent} text={t('contextSidebar.settings.backgroundColor')} />
          <CategoryAreaBackgroundImageSettings pinComponent={pinComponent} />
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>

      <ContextSidebarCollapsibleGroup
        persistId="categoryAreaBorderSettings"
        title={t('contextSidebar.settings.border')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="y" spacing={spacing.size8}>
          <BorderStyleSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-style-setting`} />
          <BorderSizeSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-size-setting`} />
          <BorderColorSetting
            pinComponent={pinComponent}
            key={`${pinComponent.id}-border-color-setting`}
            text={t('contextSidebar.settings.borderColor')}
          />
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>

      <ContextSidebarCollapsibleGroup
        persistId="categoryAreaCornerRadiusSettings"
        title={t('contextSidebar.settings.cornerRadius')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <BorderRadiusSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-radius-setting`} />
      </ContextSidebarCollapsibleGroup>
    </>
  );
};

const StyledCategoryRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.size8};
`;

const StyledTextFieldWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;
